import { Box, Typography, useTheme, Drawer, Checkbox, FormControlLabel, FormGroup, IconButton } from '@mui/material';
import { useGetRetrieveChunk } from 'apis/business/setting/knowledgeBase/hook';
import Button from 'components/Common/Button';
import Input from 'components/Common/Input';
import { useBusiness } from 'providers/context/BusinessProvider';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { RxCross1 } from 'react-icons/rx';
import { MdOutlineSchool } from 'react-icons/md';
import { IoEyeOutline } from 'react-icons/io5';
import { BuilderEmptyStateView } from '@/components/Common/BuilderEmptyStateView';

const TYPE_OPTIONS = [
  { label: 'Q&A', value: 'qa' },
  { label: 'Website', value: 'website' },
  { label: 'Document', value: 'document' }
];

export default function RetrievalTestingButton({
  knowledgeBaseId,
  openModal,
  onClose
}: {
  openModal?: boolean;
  onClose?: () => void;
  knowledgeBaseId: any;
}) {
  const [open, setOpen] = useState(false);
  const [referenceOpen, setReferenceOpen] = useState(false);
  const theme = useTheme();
  const { selectedLocation } = useBusiness();

  const [submitted, setSubmitted] = useState<any | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

  const { control, handleSubmit, watch, reset } = useForm({
    defaultValues: {
      query: '',
      datasource_types: []
    }
  });

  const isDrawerOpen = onClose ? openModal : open;

  const { data: retrieveChunk, isLoading: retrievingChunk } = useGetRetrieveChunk(knowledgeBaseId, submitted, {
    queryKey: ['retrieveChunk', selectedLocation, knowledgeBaseId, submitted],
    enabled: Boolean(selectedLocation && knowledgeBaseId && submitted)
  });

  const handleTypeChange = (type: string) => {
    setSelectedTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  };

  const onSubmit = (data?: any) => {
    const payload = {
      ...(data ?? watch()),
      datasource_types: selectedTypes
    };
    setSubmitted(payload);
  };

  const handleClose = () => {
    reset({
      query: '',
      datasource_types: []
    });

    setSubmitted(null);
    setSelectedTypes([]);
    setReferenceOpen(false);

    if (onClose) {
      onClose();
    } else {
      setOpen(false);
    }
  };

  const sources = retrieveChunk?.data?.sources ?? [];
  const answer = retrieveChunk?.data?.answer;

  return (
    <>
      {!onClose && (
        <Button
          variant='outlined'
          startIcon={<MdOutlineSchool />}
          onClick={() => setOpen(true)}
          sx={{ bgcolor: '#fff', px: 2, borderWidth: '1px' }}
        >
          Test
        </Button>
      )}

      {/* MAIN DRAWER */}
      <Drawer
        anchor='right'
        open={isDrawerOpen}
        onClose={handleClose}
        ModalProps={{ keepMounted: false }} // ensures fresh mount
      >
        <Box width={600} height='100%' display='flex' flexDirection='column'>
          {/* Header */}
          <Box display='flex' justifyContent='space-between' alignItems='center' borderBottom='1px solid #ececec' p={2}>
            <Typography variant='h6'>Retrieval Testing</Typography>
            <IconButton onClick={handleClose}>
              <RxCross1 size={18} />
            </IconButton>
          </Box>

          {/* FORM */}
          <Box
            bgcolor={theme.palette.background.paper}
            px={3}
            component='form'
            onSubmit={handleSubmit(onSubmit)}
            mt={2}
          >
            <FormGroup row sx={{ mb: 2 }}>
              {TYPE_OPTIONS.map((type) => (
                <FormControlLabel
                  key={type.value}
                  control={
                    <Checkbox
                      checked={selectedTypes.includes(type.value)}
                      onChange={() => handleTypeChange(type.value)}
                    />
                  }
                  label={type.label}
                />
              ))}
            </FormGroup>

            <Input name='query' label='Question' control={control} fullWidth />

            <Button type='submit' isPending={retrievingChunk} sx={{ mt: 3 }}>
              Test
            </Button>
          </Box>

          {/* RESULTS */}
          {retrieveChunk?.data ? (
            <Box mt={2} mx={2.3} flex={1} overflow='auto'>
              {!retrieveChunk?.data?.sources?.length ? (
                <BuilderEmptyStateView message='Run a test to see results' />
              ) : (
                <>
                  <Box display='flex' alignItems='center' justifyContent='space-between' mb={1}>
                    <Typography fontWeight={600} fontSize={16}>
                      Answer
                    </Typography>

                    <IconButton
                      size='small'
                      onClick={() => setReferenceOpen(true)}
                      sx={{
                        backgroundColor: '#EEF2FF',
                        '&:hover': { backgroundColor: '#E0E7FF' }
                      }}
                    >
                      <IoEyeOutline size={18} color='#4F46E5' />
                    </IconButton>
                  </Box>

                  <Box
                    sx={{
                      border: '1px solid #E5E7EB',
                      borderRadius: '10px',
                      padding: '12px'
                    }}
                  >
                    <Typography fontSize={14} sx={{ whiteSpace: 'pre-wrap' }}>
                      {answer}
                    </Typography>
                  </Box>

                  <Typography fontSize={12} color='text.secondary'>
                    The agent will use this answer.
                  </Typography>
                </>
              )}
            </Box>
          ) : (
            ''
          )}
        </Box>
      </Drawer>

      {/* REFERENCES DRAWER */}
      <Drawer anchor='right' open={referenceOpen} onClose={() => setReferenceOpen(false)}>
        <Box width={500} height='100%' display='flex' flexDirection='column'>
          <Box display='flex' justifyContent='space-between' alignItems='center' borderBottom='1px solid #ececec' p={2}>
            <Typography variant='h6'>References</Typography>
            <IconButton onClick={() => setReferenceOpen(false)}>
              <RxCross1 size={18} />
            </IconButton>
          </Box>

          <Box p={2} flex={1} overflow='auto'>
            {sources.length === 0 ? (
              <Typography>No references found.</Typography>
            ) : (
              sources.map((src: any) => (
                <Box key={src.id} mb={2} p={2} border='1px solid #E5E7EB' borderRadius='10px' bgcolor='#fff'>
                  <Typography pb={1} mb={1} borderBottom='1px solid #ececec'>
                    {(src.score * 100).toFixed(2)} Score
                  </Typography>

                  <Typography fontSize={16} sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {src.text}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
